import { useState, useCallback, useRef, useEffect } from 'react';
import { io } from 'socket.io-client';

export const useSpeechRecognition = () => {
    const [isRecording, setIsRecording] = useState(false);
    const [transcription, setTranscription] = useState('');
    const [partialTranscript, setPartialTranscript] = useState('');
    const [finalTranscript, setFinalTranscript] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const socketRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        // Initialize socket connection when component mounts
        initializeSocket();
        
        return () => cleanup();
    }, []);

    useEffect(() => {
        const fullTranscript = finalTranscript + (partialTranscript ? ' ' + partialTranscript : '');
        setTranscription(fullTranscript.trim());
    }, [finalTranscript, partialTranscript]);

    const cleanup = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
        setIsConnected(false);
        setIsRecording(false);
    };

    const initializeSocket = useCallback(() => {
        if (!socketRef.current) {
            socketRef.current = io(process.env.REACT_APP_SERVER_URL);

            socketRef.current.on('connect', () => {
                console.log('Connected to WebSocket server');
                socketRef.current.emit('start_recording');
            });

            socketRef.current.on("connected", async () => {
                console.log("Connected to Deepgram");
                setIsConnected(true);
                setIsRecording(true);
                const stream = await navigator.mediaDevices.getUserMedia({ 
                    audio: {
                        sampleRate: 16000,
                        channelCount: 1,
                        echoCancellation: true,
                        noiseSuppression: true
                    }
                });
                streamRef.current = stream;
                const mediaRecorder = new MediaRecorder(stream, {
                    mimeType: 'audio/webm',
                });
                mediaRecorderRef.current = mediaRecorder;
                mediaRecorder.ondataavailable = async (event) => {
                    if (event.data.size > 0 && socketRef.current?.connected) {
                        try {
                            const buffer = await event.data.arrayBuffer();
                            const base64Data = arrayBufferToBase64(buffer);
                            const payload = {
                                type: 'audio',
                                data: base64Data
                            };
                            socketRef.current.emit("message", JSON.stringify(payload));
                        } catch (error) {
                            console.error('Error converting audio data:', error);
                            setError(error);
                        }
                    }
                };
                mediaRecorder.start(250);
            });

            socketRef.current.on('disconnect', () => {
                console.log('Disconnected from WebSocket server');
                cleanup();
            });

            socketRef.current.on('transcription', (data) => {
                if (data.is_final) {
                    setFinalTranscript(prev => prev + (prev ? ' ' : '') + data.text);
                    setPartialTranscript('');
                } else {
                    setPartialTranscript(data.text);
                }
            });

            socketRef.current.on('error', (error) => {
                console.error('Server error:', error);
                setError(error);
                cleanup();
            });
        }
    }, []);
    const arrayBufferToBase64 = (buffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }
    const startRecording = useCallback(async () => {
        try {
            setError(null);
            setFinalTranscript('');
            setPartialTranscript('');
            if (!socketRef.current?.connected) {
                console.log('Socket not connected, reconnecting...');
                initializeSocket();
                return;
            }
            socketRef.current?.emit("connect_to_deepgram");
        } catch (error) {
            console.error('Error starting recording:', error);
            setError(error);
            setIsRecording(false);
        }
    }, [isConnected, initializeSocket]);

    const stopRecording = useCallback(() => {
        socketRef.current.emit("disconnect_to_deepgram");
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
        }
        // if (socketRef.current?.connected) {
        //     socketRef.current.emit('stop_recording');
        //     socketRef.current.disconnect()
        //     socketRef.current = null
        // }
        setIsRecording(false);
        // setIsConnected(false);
    }, [isRecording]);

    return {
        isRecording,
        isConnected,
        error,
        transcription,
        startRecording,
        stopRecording
    };
};